import { createContext, useContext, useReducer } from 'react';
import { getConfigObject } from '../../../utils/config';
import * as actions from './actions';

const SidePanelStateContext = createContext(null);
const SidePanelDispatchContext = createContext(null);

const localStorageKey = 'side-panel-state';

const initialState = {
  open: false,
  type: '',
  resourceId: '',
  studentId: '',
  courseId: '',
  actualCourseId: '',
  isgUrl: '',
  hasResponses: false,
  previousFocusSelector: null, // Selector to restore focus when panel closes
  mediaQueries: {
    medium: 909,
    small: 709,
    mobileOnly: 470,
  },
};

function reducer(state, action) {
  const { payload, type } = action;
  switch (type) {
    case actions.TOGGLE_SIDE_PANEL: {
      return {
        open: payload.open,
        type: payload.type,
      };
    }
    case actions.CLOSE_SIDE_PANEL: {
      // Preserve previousFocusSelector so focus can be restored after close
      const newState = {
        ...initialState,
        previousFocusSelector: state.previousFocusSelector,
      };
      localStorage.setItem(localStorageKey, JSON.stringify(newState));
      return newState;
    }
    case actions.SET_PANEL_PARAMS: {
      const newState = {
        ...state,
        open: payload.open,
        type: payload.type,
        resourceId: payload.resourceId,
        studentId: payload.studentId,
        courseId: payload.courseId,
        actualCourseId: payload.actualCourseId,
        isgUrl: payload.isgUrl,
        hasResponses: payload.hasResponses,
        previousFocusSelector: payload.previousFocusSelector || null,
      };
      localStorage.setItem(
        localStorageKey,
        JSON.stringify({ ...state, ...newState }),
      );
      return newState;
    }
    case actions.SET_MEDIA_QUERIES: {
      return {
        ...state,
        mediaQueries: payload.mediaQueries,
      };
    }
    default: {
      return state;
    }
  }
}

function init(state) {
  const { FF_SIDE_PANEL_ACTIVE = false } = getConfigObject();
  if (!FF_SIDE_PANEL_ACTIVE) {
    localStorage.removeItem(localStorageKey);
    return state;
  }
  const persistentState = JSON.parse(localStorage.getItem(localStorageKey));
  return { ...state, ...persistentState };
}

function SidePanelProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState, init);
  return (
    <SidePanelStateContext.Provider value={state}>
      <SidePanelDispatchContext.Provider value={dispatch}>
        {children}
      </SidePanelDispatchContext.Provider>
    </SidePanelStateContext.Provider>
  );
}

const useSidePanelStore = () => [
  useContext(SidePanelStateContext),
  useContext(SidePanelDispatchContext),
];

export { SidePanelProvider, useSidePanelStore };
